import React, { useRef, useEffect } from 'react';
import { 
    Box, 
    List, 
    ListItem, 
    ListItemIcon, 
    ListItemText, 
    Typography, 
    useTheme, 
    useMediaQuery,
    Divider
} from '@mui/material';
import { motion, useAnimation, useInView } from 'framer-motion';
import PropTypes from 'prop-types';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

/**
 * StyledList - A simplified list component with 2 variants
 * - With secondary content: Styled for detailed items (primary + secondary)
 * - Without secondary content: Compact style for simple items (primary only)
 */
const StyledList = ({
    items = [],
    title,
    spacing = 'medium',
    dense = false,
    maxWidth,
    animate = true,
    className,
    sx = {},
    itemSx = {},
    onItemClick,
    removeFirstItemPaddingTop = false,
    ...props
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Check if any item has secondary content to determine variant
    const hasSecondaryContent = items.some(item => 
        typeof item === 'object' && item.secondary
    );const ref = useRef(null);
    const controls = useAnimation();
    const isInView = useInView(ref, { threshold: 0.1 });

    useEffect(() => {
        if (animate && isInView) {
            controls.start("visible");
        }
    }, [isInView, controls, animate]);

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                duration: 0.6,
                ease: "easeOut",
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, x: -20 },
        visible: {
            opacity: 1,
            x: 0,
            transition: {
                duration: 0.4,
                ease: "easeOut"
            }
        }
    };    // Simple bullet point icon
    const getIcon = () => {
        if (!hasSecondaryContent) {
            // No icon for compact variant
            return null;
        }
        return (
            <FiberManualRecordIcon 
                sx={{ 
                    fontSize: '0.5rem',
                    color: theme.palette.primary.main
                }} 
            />
        );
    };// Spacing configuration
    const getSpacing = () => {
        // Different spacing for with/without secondary content
        if (hasSecondaryContent) {
            // With secondary content: normal spacing for detailed items
            switch (spacing) {
                case 'compact':
                    return { py: 0.5, px: isMobile ? 1 : 2 };
                case 'large':
                    return { py: isMobile ? 1.5 : 2, px: isMobile ? 2 : 3 };
                case 'medium':
                default:
                    return { py: isMobile ? 1 : 1.5, px: isMobile ? 1.5 : 2 };
            }
        } else {
            // Without secondary content: more compact spacing for simple items
            switch (spacing) {
                case 'compact':
                    return { py: 0.25, px: isMobile ? 0.5 : 1 };
                case 'large':
                    return { py: isMobile ? 0.75 : 1, px: isMobile ? 1 : 1.5 };
                case 'medium':
                default:
                    return { py: isMobile ? 0.5 : 0.75, px: isMobile ? 0.75 : 1 };
            }
        }
    };

    const spacingProps = getSpacing();    // Container styles based on variant (with/without secondary content)
    const getContainerStyles = () => {
        if (hasSecondaryContent) {
            // With secondary content: normal container style for detailed items
            return {
                backgroundColor: 'transparent',
                padding: theme.spacing(1)
            };
        } else {
            // Without secondary content: improved compact design with neutral colors
            return {
                backgroundColor: theme.palette.grey[100],
                border: `1px solid ${theme.palette.grey[300]}`,
                borderLeft: `4px solid ${theme.palette.grey[600]}`,
                borderRadius: theme.spacing(1),
                padding: theme.spacing(2, 2.5),
                marginLeft: 0,
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                '&:hover': {
                    backgroundColor: theme.palette.grey[50],
                    borderLeftColor: theme.palette.grey[700],
                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
                }
            };
        }
    };return (        <Box
            ref={ref}
            className={className}
            sx={{
                maxWidth: maxWidth || '100%',
                width: '100%',
                ...sx
            }}
            {...props}
        >            {title && (
                <Typography
                    variant="h6"
                    component="h3"
                    sx={{
                        fontFamily: theme.typography.fontFamily,
                        fontWeight: theme.typography.fontWeightSemiBold,
                        color: theme.palette.text.primary,
                        marginBottom: theme.spacing(1)
                    }}
                >
                    {title}
                </Typography>
            )}            <List
                component={animate ? motion.ul : 'ul'}
                initial={animate ? "hidden" : undefined}
                animate={animate ? controls : undefined}
                variants={animate ? containerVariants : undefined}
                dense={dense}
                sx={getContainerStyles()}
            >
                {items.map((item, index) => (
                    <React.Fragment key={item.id || index}>                        <ListItem
                            component={animate ? motion.li : 'li'}
                            variants={animate ? itemVariants : undefined}
                            onClick={() => onItemClick && onItemClick(item, index)}
                            sx={{
                                ...spacingProps,
                                ...(removeFirstItemPaddingTop && index === 0 && { pt: 0 }),
                                alignItems: 'flex-start',
                                borderRadius: theme.shape.borderRadius,
                                marginBottom: theme.spacing(0.5),
                                cursor: onItemClick ? 'pointer' : 'default',
                                '&:last-child': {
                                    marginBottom: 0
                                },
                                ...itemSx
                            }}
                        ><ListItemIcon
                                sx={{
                                    minWidth: hasSecondaryContent ? 20 : 0, // No space for icon in compact variant
                                    mt: 0,
                                    mr: hasSecondaryContent ? 2 : 0,
                                    color: hasSecondaryContent ? theme.palette.primary.main : 'transparent',
                                    display: hasSecondaryContent ? 'flex' : 'none', // Hide completely for compact variant
                                    justifyContent: 'flex-start',
                                    alignItems: 'center',
                                    alignSelf: 'center'
                                }}
                            >
                                {getIcon()}
                            </ListItemIcon>
                            
                            <ListItemText                                primary={
                                    <Typography
                                        variant={dense ? "body2" : "body1"}
                                        sx={{
                                            fontFamily: theme.typography.fontFamily,
                                            fontWeight: (typeof item === 'object' && item.secondary) 
                                                ? theme.typography.fontWeightMedium 
                                                : hasSecondaryContent 
                                                    ? theme.typography.fontWeightRegular
                                                    : theme.typography.fontWeightMedium, // Slightly bolder for compact variant
                                            color: item.color || (hasSecondaryContent 
                                                ? theme.palette.text.primary 
                                                : theme.palette.grey[800]), // Neutral color for compact variant
                                            lineHeight: 1.6,
                                            fontSize: {
                                                xs: theme.typography.pxToRem((typeof item === 'object' && item.secondary) ? 16 : 15),
                                                sm: theme.typography.pxToRem((typeof item === 'object' && item.secondary) ? 18 : 17)
                                            }
                                        }}
                                    >
                                        {typeof item === 'string' ? item : item.primary || item.text}
                                    </Typography>
                                }
                                secondary={
                                    (typeof item === 'object' && item.secondary) ? (
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontFamily: theme.typography.fontFamily,
                                                color: theme.palette.text.secondary,
                                                mt: 1,
                                                fontSize: theme.typography.pxToRem(dense ? 13 : 14),
                                                lineHeight: 1.6
                                            }}
                                        >
                                            {item.secondary}
                                        </Typography>
                                    ) : null
                                }
                            />
                        </ListItem>                        {/* Add dividers between all items */}
                        {index < items.length - 1 && (
                            <Divider 
                                sx={{ 
                                    mx: hasSecondaryContent ? theme.spacing(2) : theme.spacing(0),
                                    backgroundColor: hasSecondaryContent ? theme.palette.divider : theme.palette.grey[300],
                                    opacity: hasSecondaryContent ? 1 : 0.7,
                                    my: hasSecondaryContent ? 0 : theme.spacing(0.5) // Add vertical margin for compact variant
                                }} 
                            />
                        )}
                    </React.Fragment>
                ))}
            </List>
        </Box>
    );
};

StyledList.propTypes = {
    /** Array of list items. Can be strings or objects with primary, secondary, text, color properties */
    items: PropTypes.arrayOf(
        PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({
                id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                primary: PropTypes.string,
                secondary: PropTypes.string,
                text: PropTypes.string,
                color: PropTypes.string
            })
        ])
    ).isRequired,
    
    /** Title for the list - determines variant (with title = styled, without = minimal) */
    title: PropTypes.string,
    
    /** Spacing between items */
    spacing: PropTypes.oneOf(['compact', 'medium', 'large']),
    
    /** Dense layout */
    dense: PropTypes.bool,
    
    /** Maximum width of the list */
    maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    
    /** Enable animations */
    animate: PropTypes.bool,
    
    /** Additional CSS class */
    className: PropTypes.string,
    
    /** Custom styles for the list container */
    sx: PropTypes.object,
      /** Custom styles for list items */
    itemSx: PropTypes.object,
    
    /** Remove padding top from the first item */
    removeFirstItemPaddingTop: PropTypes.bool,
    
    /** Callback when item is clicked */
    onItemClick: PropTypes.func
};

export default StyledList;
